import { OrderTemplateLineType } from '../OrderTemplates/OrderTemplate';
import { PermissionArrayType } from '../../../../types/other';
import { getAllPermissionRolesForUserLevel, PERMISSIONROLECONSTANTS } from '../../../../features/utils/utils.action';
import { OrderTemplateType } from '../../../../types/order/order.type';
import { ModelType } from '../../../../types/inventory/inventory.types';
import * as yup from 'yup';
import { CountryType } from '../../../../types/country/country.type';
import { AddressType } from '../../../../components/Address/Address.types';
import { ADDRESS_STATUS } from '../../../../types/enums/address-status.enum';
import { UserType } from '../../../../types/user/user.type';

export type OrderForm = {
  step: 'order' | 'shipping';
  resellerOrganization: { id: number; name: string } | null;
  resellerUser: { id: number; email: string } | null;
  ccNotifyUsers: Array<{ id: number; name: string; email: string; firstname: string; lastname: string }>;
  organization: { id: number; name: string } | null;
  warehouse: { id: number; name: string } | null;
  qualityChecker: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'email'> | null;
  invoiceChecker: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'email'> | null;
  paymentChecker: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'email'> | null;
  invoiceNumber?: string;
  invoiceFile?: File | null;
  paymentNumber?: string;
  location: {
    id: number;
    name: string;
    equipment_groups: Array<{ id: number; description: string }>;
    address?: Omit<AddressType, 'country'> & {
      country: Pick<CountryType, 'id' | 'code2' | 'code3' | 'name'>;
      zip_code?: string;
    };
  } | null;
  address: AddressType;
  equipmentGroup: { id: number; name: string } | null;
  // ORDER TEMPLATE
  orderTemplate: Omit<OrderTemplateType, 'organization'> | null;
  useOrderTemplate: string;
  potsCastModel: ModelType | null;
  lineTypesKnown: string;
  enclosureNeeded: string;
  enclosureModel: { id: number; name: string } | null;
  routerNeeded: string;
  routerModel: { id: number; name: string } | null;
  simCardsNeeded: string;
  simCardsModel: { id: number; name: string } | null;
  nonSerializedItems: Array<{
    model: { id: number; name: string } | null;
    count: number;
  }>;
  accessoryBundles?: Array<{
    bundle: { id: number; name: string };
    quantity: number;
  }>;
  lineTypes: Array<OrderTemplateLineType>;
  shippingToLocations: { id: string; name: string } | null;
  shippingApproval: { id: string; name: string } | null;
  status?: string | null;
  shippingPackages: Array<ShippingPackageFormType>;
  purchaseEquipment: { id: string; name: string } | null;
};

export type ShippingPackageFormType = {
  carrier: { id: number; name: string } | null;
  priority: { id: string; name: string } | null;
  instructions: string;
  shippingDate: string;
  trackingNumber: string | null;
  name: string;
  id: number;
  status?: string | null;
  nonSerializedItems?: Array<{
    id?: number;
    model: { id: number; name: string; brand?: { name: string } };
    count: number;
  }>;
};

export const shippingPackageDetailsValidation = yup.object({
  name: yup.string().required('Package name is required'),
  id: yup.number().nullable(true),
  carrier: yup.mixed().test({
    name: 'carrier-conditional',
    message: 'Carrier is required when priority or shipping date is provided',
    test: function (value) {
      const { priority, shippingDate } = this.parent;
      const hasOtherFields = priority || shippingDate;
      return hasOtherFields ? !!value : true;
    },
  }),
  priority: yup.mixed().test({
    name: 'priority-conditional',
    message: 'Priority is required when carrier or shipping date is provided',
    test: function (value) {
      const { carrier, shippingDate } = this.parent;
      const hasOtherFields = carrier || shippingDate;
      return hasOtherFields ? !!value : true;
    },
  }),
  shippingDate: yup.string().test({
    name: 'shippingDate-conditional',
    message: 'Shipping date is required when carrier or priority is provided',
    test: function (value) {
      const { carrier, priority } = this.parent;
      const hasOtherFields = carrier || priority;
      return hasOtherFields ? !!value : true;
    },
  }),
  trackingNumber: yup.string().nullable(true),
  instructions: yup.string().nullable(true),
  status: yup.string().nullable(true),
});

export const orderPermissions: PermissionArrayType = {
  CREATE: [
    PERMISSIONROLECONSTANTS.superadmin,
    ...getAllPermissionRolesForUserLevel('telcloud', ['admin', 't3_admin']),
    ...getAllPermissionRolesForUserLevel('reseller', ['owner', 'admin']),
  ],
  READ: [
    PERMISSIONROLECONSTANTS.superadmin,
    ...getAllPermissionRolesForUserLevel('telcloud'),
    ...getAllPermissionRolesForUserLevel('reseller'),
    ...getAllPermissionRolesForUserLevel('customer'),
  ],
  UPDATE: [
    PERMISSIONROLECONSTANTS.superadmin,
    ...getAllPermissionRolesForUserLevel('telcloud', ['admin', 'user', 't3_admin']),
    ...getAllPermissionRolesForUserLevel('reseller', ['owner', 'admin', 'user']),
  ],
  DELETE: [PERMISSIONROLECONSTANTS.superadmin, PERMISSIONROLECONSTANTS.telcloud_admin],
};

export const orderValidationSchema = yup.object({
  // RESELLER
  resellerOrganization: yup.mixed().nullable().required('Required'),
  resellerUser: yup.mixed().nullable().required('Required'),
  ccNotifyUsers: yup.array().of(
    yup.object().shape({
      email: yup.string().required('Required'),
    })
  ),

  // CUSTOMER
  organization: yup.mixed().nullable().required('Required'),
  location: yup.mixed().nullable().required('Required'),
  equipmentGroup: yup.mixed().nullable().required('Required'),

  // ORDER
  lineTypesKnown: yup.string().required('Required'),
  useOrderTemplate: yup.string().required('Required'),
  orderTemplate: yup.mixed().when(['useOrderTemplate'], {
    is: (useOrderTemplate: string) => useOrderTemplate === '1',
    then: yup.mixed().required('Required'),
    otherwise: yup.mixed().nullable(),
  }),
  potsCastModel: yup.mixed().required('Required'),
  purchaseEquipment: yup.mixed().required('Required'),
  enclosureNeeded: yup.string().required('Required'),
  enclosureModel: yup.mixed().when(['enclosureNeeded'], {
    is: (enclosureNeeded: string) => enclosureNeeded === '1',
    then: yup.mixed().required('Required'),
    otherwise: yup.mixed().nullable(),
  }),
  routerNeeded: yup.string().required('Required'),
  routerModel: yup.mixed().when(['routerNeeded'], {
    is: (routerNeeded: string) => routerNeeded === '1',
    then: yup.mixed().required('Required'),
    otherwise: yup.mixed().nullable(),
  }),
  simCardsNeeded: yup.string().required('Required'),
  simCardsModel: yup.mixed().when(['simCardsNeeded'], {
    is: (simCardsNeeded: string) => simCardsNeeded === '1',
    then: yup.mixed().required('Required'),
    otherwise: yup.mixed().nullable(),
  }),
  lineTypes: yup.array().of(
    yup.object().shape({
      enableLine: yup.boolean(),
      profile: yup.mixed().when(['enableLine'], {
        is: (enableLine: string) => enableLine,
        then: yup.mixed().required('Required'),
        otherwise: yup.mixed().nullable(),
      }),
      provider: yup.mixed().when(['enableLine'], {
        is: (enableLine: boolean) => enableLine,
        then: yup.mixed().required('Required'),
        otherwise: yup.mixed().nullable(),
      }),
      system: yup.mixed().when(['enableLine'], {
        is: (enableLine: boolean) => enableLine,
        then: yup.mixed().required('Required'),
        otherwise: yup.mixed().nullable(),
      }),
    })
  ),

  // Address
  shippingApproval: yup.mixed().when(['step'], {
    is: (step: 'order' | 'shipping') => step === 'shipping',
    then: yup.mixed().required('Required'),
    otherwise: yup.mixed().nullable(),
  }),
  address: yup.object().when(['step', 'shippingToLocations'], {
    is: (
      step: 'order' | 'shipping',
      shippingToLocations: null | {
        id: string;
        name: string;
      }
    ) => step === 'shipping' && shippingToLocations && shippingToLocations.id === 'no',
    then: yup.object().shape({
      deliverabilityStatus: yup.number().test({
        name: 'Required ',
        message: 'Address must be validated correctly',
        test: function (value) {
          if (value === undefined) return false;
          return ![ADDRESS_STATUS.UNDEFINED, ADDRESS_STATUS.NOT_VALIDATED].includes(value);
        },
      }),
    }),
    otherwise: yup.object().nullable(),
  }),
});
